import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  User,
  Calendar,
  MapPin,
  Shield,
  Stethoscope,
  Hash,
  ArrowRight,
  CheckCircle2,
  FileImage,
  FileText,
  Camera,
  Upload,
  Loader2,
  Sparkles,
  ArrowLeft,
  Paperclip,
  AlertTriangle,
} from 'lucide-react';
import { ScheduledCase } from '@/lib/templates/types';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { getPacketClinicalText } from '@/lib/demo/orthopedicPatientPackets';
import { hasMatchingAttachment } from '@/lib/surgeon-console/attachmentMatcher';

/* ── Mock: procedure → required documents ── */
function getRequiredDocuments(procedure: string | undefined): string[] {
  if (!procedure) return ['Clinical Notes'];
  const lower = procedure.toLowerCase();
  if (lower.includes('knee arthroscopy') || (lower.includes('arthroscopic') && lower.includes('knee')))
    return ['MRI Report', 'Physical Therapy Notes (6+ weeks)'];
  if (lower.includes('total knee') || lower.includes('arthroplasty'))
    return ['Weight-Bearing X-Rays', 'Conservative Care / NSAID History'];
  if (lower.includes('rotator cuff') || lower.includes('shoulder'))
    return ['MRI Report', 'Physical Therapy Notes (6+ weeks)', 'Injection History'];
  if (lower.includes('hip'))
    return ['Weight-Bearing X-Rays', 'Physical Therapy Notes'];
  if (lower.includes('spine') || lower.includes('lumbar') || lower.includes('cervical'))
    return ['MRI Report', 'Conservative Care Documentation (12+ weeks)'];
  return ['Clinical Notes', 'Imaging Report'];
}

type ModalStep = 'demographics' | 'upload' | 'processing' | 'complete';

interface PatientConfirmDialogProps {
  patient: ScheduledCase | null;
  open: boolean;
  onConfirm: (patient: ScheduledCase, clinicalContext?: string) => void;
  onCancel: () => void;
}

export function PatientConfirmDialog({
  patient,
  open,
  onConfirm,
  onCancel,
}: PatientConfirmDialogProps) {
  const [selectedSex, setSelectedSex] = useState<string | null>(null);
  const [step, setStep] = useState<ModalStep>('demographics');
  const [uploadedFiles, setUploadedFiles] = useState<{ name: string; type: string }[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const requiredDocs = getRequiredDocuments(patient?.scheduled_procedure);
  const preLoadedDocs = patient?.pre_loaded_docs || [];
  const hasPreLoadedDocs = preLoadedDocs.length > 0;
  const requiredDocCoverage = requiredDocs.map((doc) => ({
    label: doc,
    covered: hasMatchingAttachment(doc, preLoadedDocs),
  }));
  const coveredRequiredDocCount = requiredDocCoverage.filter((doc) => doc.covered).length;

  const hasSex = !!patient?.patient_sex;
  const needsSex = !hasSex;

  const processFiles = useCallback((files: { name: string; type: string }[]) => {
    setUploadedFiles(files);
    setStep('processing');
    const delay = 1800 + Math.random() * 400;
    setTimeout(() => setStep('complete'), delay);
  }, []);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setStep('demographics');
      setSelectedSex(null);
      setUploadedFiles([]);
      setIsDragging(false);
    }
  }, [open]);

  // Paste listener for upload step
  useEffect(() => {
    if (step !== 'upload') return;
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) processFiles([{ name: `Pasted Screenshot (${new Date().toLocaleTimeString()})`, type: file.type }]);
          return;
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [step, processFiles]);

  if (!patient) return null;

  const handleConfirmDemographics = () => {
    if (hasPreLoadedDocs) {
      // Skip upload — go straight to complete with pre-loaded data
      setStep('complete');
    } else {
      setStep('upload');
    }
  };

  const handleProceed = () => {
    const finalPatient = needsSex && selectedSex ? { ...patient, patient_sex: selectedSex } : patient;
    let mockClinicalContext = '';
    if (hasPreLoadedDocs) {
      const packetText = getPacketClinicalText(finalPatient);
      mockClinicalContext = packetText
        ? `Clinical evidence pre-loaded by staff: ${preLoadedDocs.map(d => d.type).join(', ')}.\n\n${packetText}`
        : `Clinical evidence pre-loaded by staff: ${preLoadedDocs.map(d => d.type).join(', ')}. All documents verified. AI extraction complete - clinical findings available for narrative generation.`;
    } else if (uploadedFiles.length > 0) {
      mockClinicalContext = `Clinical evidence uploaded: ${uploadedFiles.map(f => f.name).join(', ')}. AI extraction complete - MRI findings and conservative therapy dates available for narrative generation.`;
    }
    onConfirm(finalPatient, mockClinicalContext);
    setStep('demographics');
    setSelectedSex(null);
    setUploadedFiles([]);
  };

  const handleCancel = () => {
    setStep('demographics');
    setSelectedSex(null);
    setUploadedFiles([]);
    onCancel();
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).map(f => ({ name: f.name, type: f.type }));
    if (files.length > 0) processFiles(files);
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files).map(f => ({ name: f.name, type: f.type })) : [];
    if (files.length > 0) processFiles(files);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleCancel(); }}>
      <DialogContent className="sm:max-w-lg">
        {/* ── STEP 1: DEMOGRAPHICS ── */}
        {step === 'demographics' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Confirm Patient Selection
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Patient Demographics</p>
                <div className="rounded-lg border p-3 space-y-1.5">
                  <p className="font-semibold text-base">{patient.patient_name}</p>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>DOB: {patient.patient_dob || '—'}</span>
                    <span>MRN: {patient.patient_mrn || '—'}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Sex: <span className="capitalize font-medium text-foreground">{patient.patient_sex || '—'}</span>
                  </div>
                </div>
              </div>

              {needsSex && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-destructive uppercase tracking-wider">Patient Sex Required</p>
                  <p className="text-xs text-muted-foreground">Sex was not provided in the patient record. Please select to proceed.</p>
                  <div className="flex gap-2">
                    <Button variant={selectedSex === 'male' ? 'default' : 'outline'} size="sm" onClick={() => setSelectedSex('male')} className="flex-1">Male</Button>
                    <Button variant={selectedSex === 'female' ? 'default' : 'outline'} size="sm" onClick={() => setSelectedSex('female')} className="flex-1">Female</Button>
                  </div>
                </div>
              )}

              <Separator />

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Hash className="w-3 h-3" />CSN / Encounter</div>
                  <p className="text-sm font-medium">{patient.encounter_id || '—'}</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Calendar className="w-3 h-3" />Scheduled Date</div>
                  <p className="text-sm font-medium">{patient.scheduled_date ? format(new Date(patient.scheduled_date), 'MMM d, yyyy h:mm a') : '—'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><MapPin className="w-3 h-3" />Facility</div>
                  <p className="text-sm font-medium">{patient.facility_name || '—'}</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Shield className="w-3 h-3" />Payer</div>
                  <p className="text-sm font-medium">{patient.payer_name || '—'}</p>
                  {patient.plan_name && <p className="text-xs text-muted-foreground">{patient.plan_name}</p>}
                </div>
              </div>

              {patient.scheduled_procedure && (
                <>
                  <Separator />
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Stethoscope className="w-3 h-3" />Scheduled Procedure</div>
                    <Badge variant="secondary" className="text-sm font-medium">{patient.scheduled_procedure}</Badge>
                  </div>
                </>
              )}

              {/* Pre-loaded docs indicator OR required docs preview */}
              {hasPreLoadedDocs ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-emerald-600" />
                    <p className="text-xs font-semibold text-emerald-800">
                      {preLoadedDocs.length} Document(s) Pre-Loaded by Staff
                    </p>
                  </div>
                  <ul className="space-y-1">
                    {preLoadedDocs.map((doc, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-emerald-700">
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                        <span>{doc.type}</span>
                        <span className="text-emerald-500 text-[10px]">• {doc.status}</span>
                      </li>
                    ))}
                  </ul>
                  <Separator className="bg-emerald-200/70" />
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-semibold text-emerald-800">
                      Required evidence covered: {coveredRequiredDocCount} of {requiredDocCoverage.length}
                    </p>
                    <ul className="space-y-1">
                      {requiredDocCoverage.map((doc) => (
                        <li
                          key={doc.label}
                          className={cn(
                            'flex items-center gap-2 text-xs',
                            doc.covered ? 'text-emerald-700' : 'text-amber-700',
                          )}
                        >
                          {doc.covered ? (
                            <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                          ) : (
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                          )}
                          <span>{doc.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-[11px] text-emerald-600 mt-1">
                    ✓ Ready for review — upload step will be skipped
                  </p>
                </div>
              ) : (
                <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3 space-y-1.5">
                  <p className="text-xs font-semibold text-sky-800">Required Clinical Evidence</p>
                  <ul className="space-y-1">
                    {requiredDocs.map((doc) => (
                      <li key={doc} className="flex items-center gap-2 text-xs text-sky-700">
                        <div className="w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0" />
                        {doc}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={handleCancel}>Cancel</Button>
              <Button onClick={handleConfirmDemographics} disabled={needsSex && !selectedSex}>
                {hasPreLoadedDocs ? (
                  <>
                    Proceed to Template
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </>
                ) : (
                  <>
                    Confirm & Upload Docs
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ── STEP 2: UPLOAD CLINICAL EVIDENCE ── */}
        {step === 'upload' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Upload Clinical Evidence
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 border">
                <User className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium truncate">{patient.patient_name}</span>
                <span className="text-xs text-muted-foreground">•</span>
                <Badge variant="secondary" className="text-[10px] shrink-0">{patient.scheduled_procedure || 'Procedure TBD'}</Badge>
              </div>

              <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3 space-y-2">
                <p className="text-xs font-semibold text-sky-900">
                  To generate a bulletproof pre-auth for{' '}
                  <span className="text-sky-700">{patient.scheduled_procedure || 'this procedure'}</span>, please upload:
                </p>
                <ul className="space-y-1.5">
                  {requiredDocs.map((doc) => (
                    <li key={doc} className="flex items-center gap-2 text-sm text-sky-800">
                      <div className="w-4 h-4 rounded border border-sky-300 bg-white shrink-0" />
                      {doc}
                    </li>
                  ))}
                </ul>
              </div>

              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'relative rounded-xl border-2 border-dashed transition-all duration-200 cursor-pointer',
                  isDragging
                    ? 'border-sky-500 bg-sky-50/80 scale-[1.01]'
                    : 'border-sky-300 bg-white hover:border-sky-500 hover:bg-sky-50/50',
                )}
              >
                <input ref={fileInputRef} type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.pages,.rtf,.txt,.xls,.xlsx,.csv" multiple onChange={handleFileSelect} />
                <div className="flex flex-col items-center gap-3 py-10 px-4 text-center">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center"><Camera className="w-5 h-5 text-sky-600" /></div>
                    <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center"><FileImage className="w-5 h-5 text-sky-600" /></div>
                    <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center"><FileText className="w-5 h-5 text-sky-600" /></div>
                  </div>
                  <p className="text-sm font-medium text-foreground">
                    {isDragging ? 'Drop files here…' : 'Drag & drop, paste (Ctrl+V), or click to upload'}
                  </p>
                  <p className="text-xs text-muted-foreground">MRI Reports, PT Notes, Face Sheets — Images or PDF</p>
                  <Button variant="outline" size="sm" type="button" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                    <Upload className="w-4 h-4 mr-1" />Browse Files
                  </Button>
                  <p className="text-[11px] text-muted-foreground">Max 20 MB per file</p>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="ghost" size="sm" onClick={() => setStep('demographics')}>
                <ArrowLeft className="w-4 h-4 mr-1" />Back
              </Button>
              <div className="flex-1" />
              <Button variant="outline" onClick={handleCancel}>Cancel</Button>
            </DialogFooter>
          </>
        )}

        {/* ── STEP 3: PROCESSING ── */}
        {step === 'processing' && (
          <div className="flex flex-col items-center justify-center gap-4 py-14 px-6 text-center">
            <div className="relative">
              <div className="w-14 h-14 rounded-full bg-sky-100 flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-sky-600 animate-spin" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-sky-600 flex items-center justify-center">
                <Sparkles className="w-3 h-3 text-white" />
              </div>
            </div>
            <div className="space-y-1.5">
              <p className="text-sm font-semibold text-foreground">Analyzing documents…</p>
              <p className="text-xs text-muted-foreground">
                Extracting MRI findings and conservative therapy dates…
              </p>
              <p className="text-[11px] text-muted-foreground mt-1">
                {uploadedFiles.map(f => f.name).join(', ')}
              </p>
            </div>
            <div className="w-48 h-1.5 rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-sky-500 rounded-full animate-pulse" style={{ width: '70%' }} />
            </div>
          </div>
        )}

        {/* ── STEP 4: COMPLETE ── */}
        {step === 'complete' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                {hasPreLoadedDocs ? 'Clinical Evidence Ready' : 'Clinical Data Extracted'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 border">
                <User className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium truncate">{patient.patient_name}</span>
              </div>

              {hasPreLoadedDocs ? (
                /* Pre-loaded docs success card */
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-emerald-600" />
                    <p className="text-sm font-semibold text-emerald-800">Clinical evidence pre-loaded by staff</p>
                  </div>
                  <ul className="space-y-1.5">
                    {preLoadedDocs.map((doc, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-emerald-700">
                        <CheckCircle2 className="w-4 h-4 shrink-0" />
                        <span>{doc.type}</span>
                        <Badge variant="outline" className="text-[9px] border-emerald-300 text-emerald-600 ml-auto">
                          {doc.status}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                  {preLoadedDocs[0]?.uploaded_by && (
                    <p className="text-[11px] text-emerald-600">
                      Uploaded by: {preLoadedDocs[0].uploaded_by}
                    </p>
                  )}
                </div>
              ) : (
                /* Surgeon-uploaded docs success */
                <>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-2">
                    <p className="text-xs font-semibold text-emerald-800">Documents processed successfully</p>
                    <ul className="space-y-1.5">
                      {uploadedFiles.map((f) => (
                        <li key={f.name} className="flex items-center gap-2 text-sm text-emerald-700">
                          <CheckCircle2 className="w-4 h-4 shrink-0" />
                          {f.name}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="rounded-lg border p-3 space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">AI Extraction Summary</p>
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-primary shrink-0" />
                      <p className="text-sm text-foreground">
                        MRI findings, conservative therapy dates, and clinical history extracted and ready for narrative generation.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={handleCancel}>Cancel</Button>
              <Button onClick={handleProceed}>
                Proceed to Template
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
